import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useDecision } from "@optimizely/react-sdk";
import { CrustOption, PizzaIngredientOption } from "@/builders/pizza/dataTransformers/builderTypes";
import { dealSelectors } from "@/builders/deals/slice/deal.slice";
import { userSelectionsSelector } from "@/builders/deals/slice/userSelections.slice";

type UseDealStepDefaultHardcodeParams = {
    sizes: PizzaIngredientOption[];
    crusts: CrustOption[];
    selectSize: (size: PizzaIngredientOption | null) => void;
    selectCrust: (crust: CrustOption | null) => void;
    skip?: boolean;
}

type DealStepDefaultOverrides = {
    dealId: string;
    stepId: string;
    override: {
        sizeId?: string;
        crustId?: string;
    };
}

const dealStepDefaultOverrides: DealStepDefaultOverrides[] = [
    { dealId: 'MEALS01UT', stepId: '344', override: { sizeId: 'CLSS001P|SIZE050M' } },
    { dealId: 'MEALS01UT', stepId: '346', override: { sizeId: 'CLSS001P|SIZE050M' } },
    { dealId: 'MEALG23CC', stepId: '326', override: { sizeId: 'CLSS001P|SIZE050M' } },
    { dealId: 'MEAL501BD', stepId: '748', override: { sizeId: 'CLSS001P|SIZE050M' } },
    { dealId: 'MEAL501BD', stepId: '749', override: { sizeId: 'CLSS001P|SIZE050M' } }
]

const useDealStepDefaultHardcode = ({ sizes, crusts, selectSize, selectCrust, skip }: UseDealStepDefaultHardcodeParams, overrides: typeof dealStepDefaultOverrides = dealStepDefaultOverrides): void => {
    const dealId = useSelector(dealSelectors.selectDealId);
    const currentStep = useSelector(userSelectionsSelector.selectCurrentStep);
    const dealOverride = overrides.find(override => override.dealId === dealId && override.stepId === currentStep?.id);
    const defaultOverride = dealOverride?.override;
    const didOverride = useRef(false);
    const sizeOverride = useMemo(() => sizes.find(size => size.id === defaultOverride?.sizeId), [defaultOverride?.sizeId, sizes]);
    const crustOverride = useMemo(() => crusts?.find(crust => crust.id === defaultOverride?.crustId), [crusts, defaultOverride?.crustId]);

    const [{ enabled: retainSizeForBundles = false }] =
        useDecision("fr-ims-649-retain-size-for-bundles");

    useEffect(() => {
        if (!retainSizeForBundles) {
            didOverride.current = false;
        }
    }, [dealId, currentStep?.id, retainSizeForBundles]);

    useEffect(() => {
        if (!retainSizeForBundles) {
            if (sizeOverride && !didOverride.current && !skip) {
                selectSize(sizeOverride);
            }
            if (crustOverride && !didOverride.current && !skip) {
                selectCrust(crustOverride);
            }

            if (!didOverride.current && (sizeOverride || crustOverride)) {
                didOverride.current = true;
            }
        }
    }, [
        crustOverride,
        selectCrust,
        selectSize,
        sizeOverride,
        skip,
        retainSizeForBundles
    ]);
};

export default useDealStepDefaultHardcode;
